/**
 * Gate 1 — Test Suites (read-only, metric-driven).
 *
 * One suite per metric that has verified test cases. Suites are auto-synced on
 * load from the backend — no manual create/edit/delete. Left rail lists metrics
 * (suite name = metric name); right panel shows verified cases for the selection.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ReactComponent as TestStrategyIcon } from "../../asserts/logo/TestStrategyIcon.svg";
import { ReactComponent as TestCasesIcon } from "../../asserts/logo/TestCasesIcon.svg";
import { ReactComponent as TestSuiteIcon } from "../../asserts/logo/TestSuiteIcon.svg";
import { ReactComponent as NoTestSuitesYetIcon } from "../../asserts/logo/no_test_suites_yet.svg";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import { GATE_SUBTABS_PL, GATE_SUBTABS_PR, GATE_SUBTABS_GAP } from "../../components/layout/layoutConstants";
import TruncatedCellLine from "../../components/TruncatedCellLine";
import { fetchTestSuites } from "../../services/testSuiteClient";
import { numberTestCases } from "../../utils/testCaseCodes";

const DOC_TABS = [
  { label: "Test Strategies", Icon: TestStrategyIcon, path: "test-strategy" },
  { label: "Test Cases", Icon: TestCasesIcon, path: "test-cases" },
  { label: "Test Suite", Icon: TestSuiteIcon, path: "test-suites" },
];
const ACTIVE_TAB_INDEX = 2;
const ACTIVE_GATE = 1;
const TAB_ACTIVE = "#555555";
const TAB_INACTIVE = "#555555";
const TAB_UNDERLINE = "#005BF0";

/**
 * Back-arrow placement — same 24px / 16px ink standard as the other Gate 1
 * headers. CSS backs out the 6px hit-target inset and Lato's ~1px capital
 * side bearing. Other header gaps stay 8px.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;

const BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const btnTextSx = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  boxSizing: "border-box",
  "& .MuiTouchRipple-root": { display: "none" },
};

/** Gate 1 solid primary (medium) — same token as Test Cases page header actions. */
const GO_TO_EXECUTION_BTN_SX = {
  ...btnTextSx,
  width: "auto",
  minWidth: 160,
  height: 40,
  minHeight: 40,
  py: "8px",
  px: "16px",
  gap: "4px",
  borderRadius: "4px",
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: PRIMARY_BG,
  border: "1px solid transparent",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: PRIMARY_BG_HOVER,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: BTN_SHADOW,
    opacity: 1,
    cursor: "not-allowed",
  },
};

/** Same ghost footer Back as Test Strategy / Gate 1 footers. */
const FOOTER_GHOST_BTN_SX = {
  ...btnTextSx,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  color: "#005BF0",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

const FOOTER_SX = {
  flexShrink: 0,
  height: 56,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: "24px",
  bgcolor: "#FFFFFF",
  boxShadow: "0px -1px 8px 0px #2122260A",
};

const BACK_TO_CASES_BTN_SX = {
  ...btnTextSx,
  width: 145,
  minWidth: 145,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  borderRadius: "4px",
  color: "#FFFFFF",
  bgcolor: "#005BF0",
  backgroundImage: "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%)",
  boxShadow: "0px 1px 2px 0px #0000000A",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:hover": {
    bgcolor: "#004ACC",
    backgroundImage: "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%)",
  },
};

const CARD_SX = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
};

/** Figma 4577:40242 "page-header" — 54px hug: 29px title + 8px gap + 17px description. */
const PAGE_HEADER_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  minWidth: 0,
};

/** Figma 4577:40247 — Lato Bold 24 / 29px box, black. */
const PAGE_TITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 24,
  fontWeight: 700,
  lineHeight: "29px",
  letterSpacing: 0,
  color: "#000000",
};

/** Figma 4577:40248 — Lato Regular 14 / 17px box, Grey-60. */
const PAGE_SUBTITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "17px",
  letterSpacing: 0,
  color: "#555555",
};

const SUITE_TITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  lineHeight: "100%",
  color: "#0F172A",
};

/** Figma 4577:40289 — "5 Test cases": count bold, label regular, Lato 14 / Grey #647488. */
const SUITE_COUNT_NUMBER_SX = {
  fontFamily: "Lato, sans-serif",
  fontStyle:"bold",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "17px",
  letterSpacing: 0,
  color: "#647488",
};

const SUITE_COUNT_LABEL_SX = {
  ...SUITE_COUNT_NUMBER_SX,
  fontWeight: 400,
  fontStyle: "normal",
};

/** Figma 4577:40286 — card-header stacks title and count with a 4px gap. */
const SUITE_HEADER_STACK_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  minWidth: 0,
};

/** Figma 4577:40295 "Horizontal/Full-width" — 1px #E2E8F0 that fills the card's
 *  content box, so it stops at the same 24px inset as the title and the table. */
const CARD_DIVIDER_SX = {
  height: "1px",
  bgcolor: "#E2E8F0",
  mx: 3,
  flexShrink: 0,
};

/** Figma left-nav Divider — 344px fill, 1px grey20. */
const NAV_DIVIDER_SX = {
  width: "100%",
  height: "1px",
  bgcolor: "#E6E6E6",
  flexShrink: 0,
};

const TABLE_HEAD_CELL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "14px",
  letterSpacing: 0,
  color: "#647488",
  bgcolor: "#FFFFFF",
  borderBottom: "1px solid #E2E8F0",
  height: 38,
  py: "12px",
  px: "16px",
  boxSizing: "border-box",
  whiteSpace: "nowrap",
};

const TABLE_BODY_CELL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "14px",
  letterSpacing: 0,
  color: "#555555",
  borderBottom: "1px solid #E2E8F0",
  height: 56,
  py: "12px",
  px: "16px",
  boxSizing: "border-box",
  verticalAlign: "middle",
};

/** Figma 4577:40306 — Lato SemiBold 13; 16px line box keeps descenders visible. */
const TEST_CASE_NAME_CELL_SX = {
  ...TABLE_BODY_CELL_SX,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "16px",
};

/** Figma — Risk column: Lato Regular 13, 16px line box. Shared by Trust Attribute,
 *  Status and Added On so every value column renders with identical type. */
const RISK_CELL_SX = {
  ...TABLE_BODY_CELL_SX,
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "16px",
};

/** Figma 4577:40314 — Validated pill; label type matches the Risk column. */
const STATUS_CHIP_SX = {
  height: "auto",
  borderRadius: "12px",
  px: "8px",
  py: "4px",
  fontFamily: RISK_CELL_SX.fontFamily,
  fontSize: RISK_CELL_SX.fontSize,
  fontWeight: RISK_CELL_SX.fontWeight,
  lineHeight: RISK_CELL_SX.lineHeight,
  letterSpacing: 0,
  "& .MuiChip-label": { px: 0, overflow: "visible", lineHeight: "inherit" },
};

const ADDED_ON_CELL_SX = {
  ...RISK_CELL_SX,
  whiteSpace: "nowrap",
};

/** Cell text inherits the column token so tooltips keep one truncated line. */
const INHERIT_TEXT_SX = {
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
  letterSpacing: "inherit",
  color: "inherit",
};

function formatAddedOn(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

/** Figma 4577:40254 "Left nav (outline)" — 360px column, white, 1px grey20 right
 *  border, scrolls vertically. Matches the Test Strategy page left nav. */
const NAV_SX = {
  width: { xs: "100%", sm: "clamp(240px, 24%, 360px)" },
  maxWidth: { xs: "100%", sm: 360 },
  flex: { xs: "0 0 auto", sm: "0 0 clamp(240px, 24%, 360px)" },
  flexShrink: 0,
  bgcolor: "#FFFFFF",
  borderRight: { xs: "none", sm: "1px solid #E6E6E6" },
  borderBottom: { xs: "1px solid #E6E6E6", sm: "none" },
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
  maxHeight: { xs: 320, sm: "none" },
  boxSizing: "border-box",
};

/** Figma — 8px padding, 4px gap between nav items. */
const NAV_SCROLL_SX = {
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  p: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const NAV_ITEM_SX = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  height: 40,
  // Without this the column flex container squashes rows instead of scrolling.
  minHeight: 40,
  flexShrink: 0,
  boxSizing: "border-box",
  px: "8px",
  py: "8px",
  borderRadius: "6px",
  borderLeft: "4px solid transparent",
  cursor: "pointer",
  bgcolor: "transparent",
  minWidth: 0,
  "&:hover": { bgcolor: "#F9FAFB" },
  "&:focus": { outline: "none" },
  "&:focus-visible": { outline: "2px solid #005CF0", outlineOffset: "2px" },
};

const NAV_ITEM_ACTIVE_SX = {
  bgcolor: "#F0F6FF",
  borderLeft: "4px solid #0046B8",
  borderRadius: 0,
  "&:hover": { bgcolor: "#F0F6FF" },
};

const NAV_TITLE_SX = {
  flex: 1,
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  letterSpacing: 0,
};

/** Grey pill carrying the case count — same token as the Test Strategy nav. */
function NavCountBadge({ n }) {
  return (
    <Box
      sx={{
        minWidth: 23,
        height: 17,
        px: "8px",
        py: "2px",
        boxSizing: "border-box",
        borderRadius: "10px",
        bgcolor: "#E6E6E6",
        color: "#555555",
        fontFamily: "Lato, sans-serif",
        fontSize: 11,
        fontWeight: 700,
        lineHeight: "100%",
        letterSpacing: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );
}

const STATUS_DISPLAY = {
  verified: { label: "Validated", bgcolor: "#E0F7ED", color: "#218C21" },
};

function GateDocTabs({ projectId, navigate }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        pl: GATE_SUBTABS_PL,
        pr: GATE_SUBTABS_PR,
        height: 50,
        minHeight: 50,
        bgcolor: "#fff",
        borderBottom: "1px solid #E6E6E6",
        flexShrink: 0,
        overflowX: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", gap: GATE_SUBTABS_GAP, minWidth: "min-content" }}>
        {DOC_TABS.map((t, i) => {
          const active = i === ACTIVE_TAB_INDEX;
          const clickable = i !== ACTIVE_TAB_INDEX;
          const Icon = t.Icon;
          return (
            <Box
              key={t.label}
              onClick={() => clickable && navigate(`/${t.path}/${projectId}`)}
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                height: "100%",
                py: "8px",
                boxSizing: "border-box",
                cursor: clickable ? "pointer" : "default",
                color: active ? TAB_ACTIVE : TAB_INACTIVE,
                "& svg": { width: 17, height: 17, display: "block", flexShrink: 0 },
                "& svg path": { stroke: "currentColor" },
                ...(active && {
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "-8px",
                    right: "-8px",
                    bottom: 0,
                    height: "3px",
                    bgcolor: TAB_UNDERLINE,
                    pointerEvents: "none",
                  },
                }),
              }}
            >
              <Icon aria-hidden />
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 14,
                  fontWeight: active ? 700 : 400,
                  lineHeight: "18px",
                  whiteSpace: "nowrap",
                }}
              >
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function TestSuitePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [suites, setSuites] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [risks, setRisks] = useState([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchTestSuites(projectId);
      if (!res?.success) {
        setError(res?.error || "Failed to load test suites.");
        return;
      }
      setSuites(res.data?.suites || []);
      setTestCases(res.data?.testCases || []);
      setRisks(res.data?.risks || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to load test suites.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    load();
  }, [projectId, load]);

  useEffect(() => {
    if (!suites.length) {
      setSelectedSuiteId("");
      return;
    }
    setSelectedSuiteId((prev) => (suites.some((s) => s.id === prev) ? prev : suites[0].id));
  }, [suites]);

  const caseById = useMemo(() => {
    const numbered = numberTestCases(testCases, risks);
    return new Map(numbered.map((tc) => [tc.id, tc]));
  }, [testCases, risks]);

  const selectedSuite = useMemo(
    () => suites.find((s) => s.id === selectedSuiteId) || null,
    [suites, selectedSuiteId]
  );

  const selectedSuiteRows = useMemo(() => {
    if (!selectedSuite) return [];
    return (selectedSuite.cases || [])
      .map((member) => {
        const testCase = caseById.get(member.testCaseId);
        return testCase ? { ...testCase, suiteAddedAt: member.addedAt } : null;
      })
      .filter(Boolean);
  }, [selectedSuite, caseById]);

  const hasVerifiedCases = testCases.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#FFFFFF", minWidth: 0 }}>
      <IntakeGateTabsBar
        activeGate={ACTIVE_GATE}
        unlockedUpTo={suites.length ? 2 : ACTIVE_GATE}
        onGateClick={(gateIndex) => {
          if (gateIndex === 0) navigate(`/chat/${projectId}`);
          if (gateIndex === 1) navigate(`/gate1/${projectId}`);
          if (gateIndex === 2) navigate(`/gate2/${projectId}`);
        }}
        showActions={false}
      />

      <GateDocTabs projectId={projectId} navigate={navigate} />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, bgcolor: "#FFFFFF" }}>
        <Box
          sx={{
            flexShrink: 0,
            boxSizing: "border-box",
            width: "100%",
            bgcolor: "#FFFFFF",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            gap: 2,
            pl: `${HEADER_INSET_LEFT_PX}px`,
            pr: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
            borderBottom: "1px solid #E6E6E6",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: `${HEADER_ROW_GAP_PX}px`,
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <IntakeHeaderBackButton
              onClick={() => navigate(`/test-cases/${projectId}`)}
              aria-label="Back to test cases"
            />
            <Box sx={{ ...PAGE_HEADER_STACK_SX, ml: `${TITLE_NUDGE_LEFT_PX}px` }}>
            <Typography sx={PAGE_TITLE_SX}>Test Suites</Typography>
            <Typography sx={PAGE_SUBTITLE_SX}>
              Assemble validated test cases into executable suites for AI model evaluation.
            </Typography>
            </Box>
          </Box>
        </Box>

        {error ? (
          <Typography sx={{ px: { xs: 2, md: 3 }, pt: 2, fontSize: 13, color: "#DE350B", fontWeight: 600 }}>
            {error}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : !hasVerifiedCases || !suites.length ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", px: 2, py: 4 }}>
            <Box sx={{ textAlign: "center", maxWidth: 520 }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <NoTestSuitesYetIcon style={{ width: 120, height: 120, display: "block" }} aria-hidden />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: "100%",
                  color: "#171C22",
                  mb: 1,
                }}
              >
                {!hasVerifiedCases ? "No verified test cases yet" : "No test suites available"}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: 16,
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#555555",
                  mb: 3,
                }}
              >
                {!hasVerifiedCases
                  ? "Test suites are created automatically from verified test cases grouped by metric. Go back to Test Cases, confirm ground truth, and verify cases first."
                  : "Verified test cases must be linked to a metric before a suite can be created. Check that your verified cases have an associated metric in the Metrics Register."}
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate(`/test-cases/${projectId}`)}
                sx={BACK_TO_CASES_BTN_SX}
              >
                Go to Test Cases
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              minHeight: 0,
              overflow: "hidden",
              bgcolor: "#FFFFFF",
            }}
          >
            <Box sx={NAV_SX}>
              <Box sx={NAV_SCROLL_SX}>
                {suites.map((suite, i) => {
                  const active = suite.id === selectedSuiteId;
                  const count = suite.caseCount ?? (suite.cases || []).length;
                  return (
                    <Box key={suite.id} sx={{ display: "contents" }}>
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedSuiteId(suite.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedSuiteId(suite.id);
                          }
                        }}
                        sx={{
                          ...NAV_ITEM_SX,
                          ...(active ? NAV_ITEM_ACTIVE_SX : null),
                        }}
                      >
                        <TruncatedCellLine
                          text={`${i + 1}. ${suite.name}`}
                          tooltipTitle={suite.name}
                          placement="right"
                          sx={{
                            ...NAV_TITLE_SX,
                            color: active ? "#0046B8" : "#383838",
                          }}
                        />
                        <NavCountBadge n={count} />
                      </Box>
                      <Box sx={NAV_DIVIDER_SX} />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflowY: "auto",
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 3 },
              }}
            >
              <Box sx={{ ...CARD_SX, minWidth: 0, width: "100%" }}>
                <Box sx={{ ...SUITE_HEADER_STACK_SX, px: 3, pt: 3, pb: 2 }}>
                  <Typography sx={SUITE_TITLE_SX}>{selectedSuite?.name}</Typography>
                  <Typography sx={SUITE_COUNT_LABEL_SX}>
                    <Box component="span" sx={SUITE_COUNT_NUMBER_SX}>
                      {selectedSuiteRows.length}
                    </Box>{" "}
                    Test case{selectedSuiteRows.length === 1 ? "" : "s"}
                  </Typography>
                </Box>

                <Box sx={CARD_DIVIDER_SX} />

                <Box sx={{ px: 3, pt: 3, pb: 3 }}>
                  <TableContainer sx={{ border: "1px solid #E2E8F0", borderRadius: "8px", overflow: "hidden" }}>
                    <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                      <colgroup>
                        <col />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 120 }} />
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={TABLE_HEAD_CELL_SX}>Test Case Name</TableCell>
                          <TableCell sx={TABLE_HEAD_CELL_SX}>Risk</TableCell>
                          <TableCell sx={TABLE_HEAD_CELL_SX}>Trust Attribute</TableCell>
                          <TableCell sx={TABLE_HEAD_CELL_SX}>Status</TableCell>
                          <TableCell sx={TABLE_HEAD_CELL_SX}>Added On</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedSuiteRows.map((tc) => {
                          const status = STATUS_DISPLAY[tc.status] || STATUS_DISPLAY.verified;
                          return (
                            <TableRow key={tc.id}>
                              <TableCell
                                sx={{
                                  ...TEST_CASE_NAME_CELL_SX,
                                  maxWidth: 260,
                                  minWidth: 0,
                                }}
                              >
                                <TruncatedCellLine text={tc.name || "—"} sx={INHERIT_TEXT_SX} />
                              </TableCell>
                              <TableCell sx={{ ...RISK_CELL_SX, maxWidth: 150, minWidth: 0 }}>
                                <TruncatedCellLine text={tc.riskName || "—"} sx={INHERIT_TEXT_SX} />
                              </TableCell>
                              <TableCell sx={{ ...RISK_CELL_SX, maxWidth: 150, minWidth: 0 }}>
                                <TruncatedCellLine text={tc.trustAttribute || "—"} sx={INHERIT_TEXT_SX} />
                              </TableCell>
                              <TableCell sx={RISK_CELL_SX}>
                                <Chip
                                  label={status.label}
                                  size="small"
                                  sx={{
                                    ...STATUS_CHIP_SX,
                                    bgcolor: status.bgcolor,
                                    color: status.color,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={ADDED_ON_CELL_SX}>
                                {formatAddedOn(tc.suiteAddedAt)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {!selectedSuiteRows.length && (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#98A2B3", fontSize: 13 }}>
                              No verified test cases for this metric.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={FOOTER_SX}>
        <Button
          disableElevation
          disableRipple
          onClick={() => navigate(`/test-cases/${projectId}`)}
          sx={FOOTER_GHOST_BTN_SX}
        >
          Back
        </Button>
        {suites.length ? (
          <Button
            variant="contained"
            disableElevation
            disableRipple
            onClick={() => navigate(`/gate2/${projectId}`)}
            sx={GO_TO_EXECUTION_BTN_SX}
          >
            Go to Execution
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
